import { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import useSearchParam, { setSearchParams } from 'hooks/useSearchParam';
import TeamMemberService from 'services/TeamMemberService';
import { monitoringExceptionHelper } from 'instrumentation/helpers';
import type { AccessStruct } from '@kizen/permissions/types';
import type { IntegrationSecret } from '@kizen/api/integration-secrets';
import { MenuAction } from './types';

export const SECRET_PARAM = 'secret';

export const integrationSecretsLinkFor = (route: string, apiName: string) =>
  `${route}?${SECRET_PARAM}=${encodeURIComponent(apiName)}`;

export const useSecretDeepLink = (
  onSelectAction: (
    action: MenuAction,
    integrationSecret: IntegrationSecret | null
  ) => void,
  access: AccessStruct
) => {
  const history = useHistory();
  const apiName = useSearchParam(SECRET_PARAM);
  const consumed = useRef(false);

  useEffect(() => {
    if (!apiName || consumed.current) {
      return;
    }

    consumed.current = true;

    (async () => {
      try {
        let secret: IntegrationSecret | undefined;

        for (let page = 1; !secret; page++) {
          const { results, next } =
            await TeamMemberService.getIntegrationSecrets({
              search: undefined,
              ordering: 'name',
              page,
              page_size: 999,
            });

          secret = results?.find(
            (result: IntegrationSecret) => result.api_name === apiName
          );

          if (!next) {
            break;
          }
        }

        if (secret) {
          onSelectAction(access.edit ? 'edit' : 'view', secret);
        }
      } catch (ex) {
        monitoringExceptionHelper(ex);
      } finally {
        setSearchParams(
          history,
          { [SECRET_PARAM]: null },
          { method: 'replace' }
        );
      }
    })();
  }, [apiName, history, onSelectAction, access.edit]);
};
